import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoaderCircle, Plus } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageHeader } from '@/components/page-header';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchPropertyTemplates } from '@/features/property-templates/propertyTemplatesThunks';
import { makeTemplateListScopeKey } from '@/features/property-templates/propertyTemplatesTypes';
import type { PropertyTemplate } from '@/domain/entities/preperty-template';
import { selectActiveOrganizationId } from '@/store/selectors/authorizationSelectors';
import { usePermissions } from '@/hooks/usePermissions';

const ADD_TEMPLATE_VALUE = '__add_template__';
type TemplateSource = 'all' | 'system' | 'organization';

export default function AddPropertyPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isSuperAdmin } = usePermissions();
  const activeOrganizationId = useAppSelector(selectActiveOrganizationId);
  const templates = useAppSelector((s) => s.propertyTemplates.itemsByStatus.published);
  const isLoading = useAppSelector((s) => s.propertyTemplates.isListingByStatus.published);
  const hasFetched = useAppSelector((s) => s.propertyTemplates.hasFetchedByStatus.published);
  const fetchedScopeKey = useAppSelector((s) => s.propertyTemplates.listScopeByStatus.published);
  const fetchError = useAppSelector((s) => s.propertyTemplates.errorByStatus.published);
  const [templateSource, setTemplateSource] = useState<TemplateSource>('all');
  const requestOrgID = isSuperAdmin && templateSource === 'all' ? undefined : (activeOrganizationId ?? undefined);
  const requestedScopeKey = useMemo(() => makeTemplateListScopeKey(requestOrgID, 'business'), [requestOrgID]);
  const hasTemplatesForRequest = hasFetched && fetchedScopeKey === requestedScopeKey;
  const requestedScopeRef = useRef<string | null>(null);

  useEffect(() => {
    if (hasTemplatesForRequest) {
      requestedScopeRef.current = null;
      return;
    }
    if (!activeOrganizationId || isLoading || requestedScopeRef.current === requestedScopeKey) return;

    requestedScopeRef.current = requestedScopeKey;
    void dispatch(fetchPropertyTemplates({ status: 'published', orgID: requestOrgID, audience: 'business' }));
  }, [activeOrganizationId, dispatch, hasTemplatesForRequest, isLoading, requestOrgID, requestedScopeKey]);

  const options = useMemo(() => {
    if (!hasTemplatesForRequest) return [];

    return (templates ?? [])
      .filter((template: PropertyTemplate) => {
        if (template.status !== 'published') return false;
        if (template.audience !== 'business' && template.audience !== 'both') return false;
        if (templateSource === 'system') return template.scope === 'system';
        if (templateSource === 'organization') {
          return template.scope === 'organization' && template.orgID === activeOrganizationId;
        }
        if (isSuperAdmin) return true;
        return template.scope === 'system' || template.orgID === activeOrganizationId;
      })
      .map((template: PropertyTemplate) => ({
        id: template.id,
        title: template.title ?? 'Untitled template',
      }));
  }, [activeOrganizationId, hasTemplatesForRequest, isSuperAdmin, templateSource, templates]);

  const [templateId, setTemplateId] = useState('');

  const handleSourceChange = (value: string) => {
    setTemplateSource(value as TemplateSource);
    setTemplateId('');
  };

  const handleTemplateChange = (value: string) => {
    if (value === ADD_TEMPLATE_VALUE) {
      navigate('/templates/add');
      return;
    }

    setTemplateId(value);
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Add New Property" description="Select a base template to seed your property." />

      <Card>
        <CardContent className="space-y-6 pt-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="templateSource">Template Source</Label>
              <Select value={templateSource} onValueChange={handleSourceChange}>
                <SelectTrigger id="templateSource" aria-label="Template Source">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="system">System Templates</SelectItem>
                  <SelectItem value="organization">Org Templates</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="templateId">Base Template</Label>
              <Select value={templateId} onValueChange={handleTemplateChange}>
                <SelectTrigger id="templateId" aria-label="Base Template">
                  <SelectValue
                    placeholder={!activeOrganizationId ? 'Select an active organization first' : 'Select a template'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {!activeOrganizationId ? (
                    <div className="text-muted-foreground px-2 py-2 text-sm">Select an active organization first.</div>
                  ) : isLoading && !hasTemplatesForRequest ? (
                    <div className="text-muted-foreground flex items-center gap-2 px-2 py-2 text-sm">
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                      Fetching templates…
                    </div>
                  ) : fetchError && !hasTemplatesForRequest ? (
                    <div className="text-destructive px-2 py-2 text-sm">Failed to fetch templates.</div>
                  ) : options.length === 0 && templateSource === 'system' ? (
                    <div className="text-muted-foreground px-2 py-2 text-sm">No published system templates.</div>
                  ) : options.length === 0 ? (
                    <SelectItem value={ADD_TEMPLATE_VALUE}>
                      <Plus className="h-4 w-4" />
                      Add Template
                    </SelectItem>
                  ) : (
                    <>
                      {isLoading ? (
                        <div className="text-muted-foreground flex items-center gap-2 px-2 py-2 text-sm">
                          <LoaderCircle className="h-4 w-4 animate-spin" />
                          Fetching templates…
                        </div>
                      ) : null}
                      {options.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.title}
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            <p className="text-muted-foreground text-xs md:col-span-2">
              Only published templates available to business users are shown. The property will be created in the active
              organization.
            </p>
          </div>

          <div className="flex justify-end">
            <Button
              size="lg"
              className="min-w-[200px]"
              disabled={!templateId}
              onClick={() => navigate(`/properties/new?templateId=${encodeURIComponent(templateId)}`)}
            >
              Continue
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
